// UNIFIED editor modal look — Configure + Attachments modals and the modern
// settings panels. This file is intentionally IDENTICAL in the std, site,
// gallery, and forum slots (slot-to-slot imports are forbidden by
// check:templates, and a shared visual tree is banned; per-slot copies are
// the documented sharing pattern — doc 29 §2 Layer C). When editing, update
// all four copies. The mailbox keeps its own dialog and skips these tokens.

import type { EditorAttachmentsUiSkin, EditorModalSkin, EditorSkin } from '@/deskUiSkeletons/editor/EditorSurfaceContext'

export const editorModalSkin: EditorModalSkin = {
  overlayOpen: 'fixed inset-0 z-[95] flex bg-[#17212b]/50 p-3 sm:p-6 lg:p-10',
  overlayClosed: 'hidden',
  dialog: 'mx-auto flex h-full min-h-0 w-full max-w-[1100px] flex-col overflow-hidden rounded-lg border border-[#c9d3dc] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.34)]',
  header: 'flex h-14 flex-[0_0_56px] items-center gap-3 border-b border-[#e2e8ee] px-5 max-[640px]:px-3',
  headerIcon: 'flex h-8 w-8 flex-none items-center justify-center rounded bg-[#e8f0ff] text-[#2563eb]',
  title: 'min-w-0 flex-1 truncate text-[16px] font-semibold text-[#17212b]',
  closeButton: 'flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded text-[20px] leading-none text-[#526272] hover:bg-[#edf2f6]',
  body: 'flex min-h-0 flex-1 overflow-hidden max-[700px]:flex-col',
  nav: 'flex w-52 flex-none flex-col gap-1 overflow-y-auto border-r border-[#e2e8ee] bg-[#f7f9fb] p-3 max-[700px]:w-full max-[700px]:flex-row max-[700px]:overflow-x-auto max-[700px]:border-b max-[700px]:border-r-0 max-[700px]:p-2',
  navItem: 'flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-[13px]',
  navItemActive: 'bg-[#e8f0ff] font-semibold text-[#2563eb]',
  navItemInactive: 'text-[#314253] hover:bg-[#edf2f6]',
  navIcon: 'h-4 w-4 flex-none',
  content: 'min-h-0 flex-1 overflow-y-auto p-5 max-[640px]:p-3',
}

export const editorAttachmentsUiSkin: EditorAttachmentsUiSkin = {
  strip: 'flex flex-wrap items-center gap-2',
  stripThumb: 'relative inline-flex',
  stripImg: 'h-16 w-16 rounded-md border border-[#d9e1e8] bg-white object-cover',
  stripRemove: 'absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-black/10 bg-white text-[13px] leading-none text-[#883333] shadow-sm hover:bg-[#fbeaea]',
  grid: 'flex flex-wrap gap-3',
  tile: 'relative flex min-h-[132px] w-[120px] flex-col gap-1.5 rounded-md border border-[#d9e1e8] bg-[#f8fafb] p-2',
  tileInactive: 'opacity-50',
  tileError: 'border-[#c65f5f]',
  preview: 'h-[96px] w-full rounded border border-[#e2e8ee] bg-white object-cover',
  name: 'overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#314253]',
  status: 'text-[11px] text-[#687482]',
  remove: 'absolute right-1 top-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-black/10 bg-white/90 text-[14px] leading-none text-[#883333] hover:bg-white',
  restore: 'cursor-pointer rounded border border-[#c8d2dc] bg-white px-2 py-1 text-xs text-[#2563eb] hover:bg-[#edf2f6]',
  uploadTile: 'flex min-h-[132px] w-[120px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-[#aab8c4] bg-white p-2 text-[#2563eb] hover:bg-[#f2f7ff]',
  uploadPreview: 'flex h-10 w-10 items-center justify-center rounded-full bg-[#e8f0ff] text-[22px] leading-none',
  uploadLabel: 'text-xs font-medium',
  empty: 'text-xs text-[#687482]',
}

// Modern settings panel tokens for the Configure modal content (the mailbox
// dialog's visual language: flat rows, blue accents). Feeds EditorSkin.settings.
export const modernSettingsSkin: EditorSkin['settings'] = {
  root: 'flex min-h-0 flex-1 flex-col gap-2 text-left text-[13px] font-[-apple-system,BlinkMacSystemFont,Segoe_UI,Arial,sans-serif]',
  tabs: 'hidden',
  tab: '',
  tabActive: '',
  tabInactive: '',
  body: 'flex min-h-0 flex-1 flex-col',
  paneHidden: 'hidden',
  panel: 'm-0 flex w-full min-w-0 max-w-[720px] flex-col gap-3 border-0 bg-white p-0',
  legend: 'mb-2 p-0 text-[18px] font-semibold text-[#17212b]',
  row: 'flex min-h-11 flex-wrap items-center gap-3 border-b border-[#e6ebf0] py-2',
  check: 'flex min-h-9 cursor-pointer items-center gap-3 font-normal text-[#314253] [&_input[type=checkbox]]:m-0 [&_input[type=checkbox]]:h-4 [&_input[type=checkbox]]:w-4 [&_input[type=checkbox]]:accent-[#2563eb]',
  rowCheck: 'flex min-h-9 flex-1 cursor-pointer items-center gap-3 font-normal text-[#314253] [&_input[type=checkbox]]:m-0 [&_input[type=checkbox]]:h-4 [&_input[type=checkbox]]:w-4 [&_input[type=checkbox]]:accent-[#2563eb]',
  field: 'flex flex-col gap-2 border-b border-[#e6ebf0] py-3 text-[#314253]',
  combo: 'h-9 min-w-[160px] max-w-full rounded border border-[#c8d2dc] bg-white px-2 text-[13px] disabled:cursor-not-allowed disabled:border-[#dbe2e8] disabled:bg-[#f1f5f9] disabled:text-[#94a3b8] disabled:opacity-70',
  textInput: 'box-border !h-9 !w-full !rounded !border !border-[#c8d2dc] !bg-white !px-3 !py-1 text-[13px]',
  timeRow: 'mb-2 flex flex-col gap-1.5',
  timeInput: 'box-border h-9 w-full cursor-pointer rounded border border-[#c8d2dc] bg-white px-3 text-[13px]',
  avatarButton: 'flex w-full cursor-pointer items-center gap-3 rounded-md border border-dashed border-[#aab8c4] bg-white p-3 text-[13px] text-[#2563eb] hover:bg-[#f2f7ff]',
  avatarImage: 'h-16 w-16 rounded border border-[#d9e1e8] bg-white object-cover',
  avatarInput: 'hidden',
  protectForm: 'contents',
}
